import React, { useContext, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Badge from '@atlaskit/badge';
import Button, { LoadingButton } from '@atlaskit/button';
import Form, { Field, FormFooter } from '@atlaskit/form';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';
import Popup, { TriggerProps } from '@atlaskit/popup';
import { Radio } from '@atlaskit/radio';

import { useAnalytics, withAnalytics } from 'src/components/settings/analytics';
import { useFlag } from 'src/hooks/flag';
import { useIntl } from 'src/hooks/intl';
import { showFlagComponent } from 'src/redux/flags';
import {
  OPEN_REVIEW_DRAFT_DIALOG,
  refreshCodeReviewData,
} from 'src/redux/pull-request/actions';
import { ITERATIVE_REVIEW_COMPLETED } from 'src/redux/pull-request/iterative-review-reducer';
import {
  getCurrentPullRequest,
  getCurrentPullRequestUrlPieces,
  getHasCurrentUserApprovedPr,
  getNumOutdatedPendingComments,
  getIsChangesRequested,
  getPullRequestSourceHash,
  getIsCurrentPullRequestAuthor,
  getIRAnalyticsTrackingAttributes,
} from 'src/redux/pull-request/selectors';
import {
  getNumPendingComments,
  getPendingTaskCount,
} from 'src/redux/pull-request/selectors/review-selectors';
import urls from 'src/redux/pull-request/urls';
import { updateLastReviewedCommit } from 'src/sections/repository/sections/pull-request/components/iterative-review/utils';
import authRequest from 'src/utils/fetch';

import {
  DiffStateContext,
  SelectedDiffType,
} from '../tabs/diff/diff-state-context';
import { KeyboardShortcutTooltip } from '../tabs/shortcuts/keyboard-shortcut-tooltip';
import { KeyboardShortcutsContext } from '../tabs/shortcuts/pull-request-keyboard-shortcuts-context';

import { OutdatedPendingCommentsModal } from './outdated-pending-comments-modal';
import messages from './review-actions.i18n';
import * as styles from './review-actions.style';

export const DiscardReview = withAnalytics(
  (): JSX.Element => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const { publishButtonClickEvent, publishTrackEvent } = useAnalytics();
    const { showSuccessFlag } = useFlag({ enableForPdvs: true });
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const { owner, slug, id } = useSelector(getCurrentPullRequestUrlPieces);

    const closeModal = () => setIsOpen(false);

    const handleDiscardReview = async () => {
      try {
        if (id == null) {
          throw new Error(
            intl.formatMessage(messages.discardNoPullRequestIdError)
          );
        }
        publishButtonClickEvent('DiscardReview');
        setIsLoading(true);

        const url = urls.api.internal.review(owner, slug, id);
        const response = await fetch(authRequest(url, { method: 'DELETE' }));

        closeModal();

        if (!response.ok) {
          setIsLoading(false);
          throw new Error(intl.formatMessage(messages.discardChangesError));
        }
        setIsLoading(false);
        showSuccessFlag('', intl.formatMessage(messages.discardReviewSuccess));

        publishTrackEvent({
          action: 'discarded',
          actionSubject: 'pullRequestReview',
        });

        dispatch(
          refreshCodeReviewData({
            needsDiff: false,
            needsPullRequest: false,
            needsComments: true,
            needsTasks: true,
            forceNeedsMergeChecks: false,
          })
        );
      } catch (e) {
        dispatch(showFlagComponent('discard-review-error'));
      }
    };

    return (
      <>
        <Button onClick={() => setIsOpen(true)}>
          {intl.formatMessage(messages.discardReview)}
        </Button>
        <ModalTransition>
          {isOpen && (
            <Modal onClose={closeModal}>
              <ModalHeader>
                <ModalTitle>
                  {intl.formatMessage(messages.discardReview)}
                </ModalTitle>
              </ModalHeader>
              <ModalBody>
                <p>{intl.formatMessage(messages.discardReviewConfirmation)}</p>
              </ModalBody>
              <ModalFooter>
                <Button
                  appearance="subtle"
                  isDisabled={isLoading}
                  onClick={closeModal}
                >
                  {intl.formatMessage(messages.cancelButton)}
                </Button>
                <LoadingButton
                  appearance="danger"
                  onClick={handleDiscardReview}
                  autoFocus
                  isLoading={isLoading}
                >
                  {intl.formatMessage(messages.discardButton)}
                </LoadingButton>
              </ModalFooter>
            </Modal>
          )}
        </ModalTransition>
      </>
    );
  },
  {
    source: 'pullRequestScreen',
  }
);

type StatusValue = 'approved' | 'changes_requested' | 'no_status';

interface ReviewStatusOptions {
  id: StatusValue;
  status: string;
  description: string;
  value: StatusValue;
}

export const FinishReview = withAnalytics(
  () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [outdatedModalOpen, setOutdatedModalOpen] = useState(false);

    const { finishReviewModalRef } = useContext(KeyboardShortcutsContext);
    if (finishReviewModalRef) {
      finishReviewModalRef.current = () => {
        if (!isOpen) setIsOpen(true);
      };
    }

    const { owner, slug, id } = useSelector(getCurrentPullRequestUrlPieces);
    const { showSuccessFlag } = useFlag({ enableForPdvs: true });
    const dispatch = useDispatch();
    const intl = useIntl();
    const {
      publishButtonClickEvent,
      publishLinkClickEvent,
      publishTrackEvent,
    } = useAnalytics();

    const numPendingComments = useSelector(getNumPendingComments);
    const numOutdatedComments = useSelector(getNumOutdatedPendingComments);
    const numPendingTasks = useSelector(getPendingTaskCount);
    const sourceBranchHash = useSelector(getPullRequestSourceHash);
    const pullRequest = useSelector(getCurrentPullRequest);
    const isPRAuthor = useSelector(getIsCurrentPullRequestAuthor);
    const { iterativeReviewSelectedDiff } = useContext(DiffStateContext);
    const irTracking = useSelector(getIRAnalyticsTrackingAttributes);
    irTracking.irSelectedDiffType =
      SelectedDiffType[iterativeReviewSelectedDiff.selectedDiffType];

    const noStatus = {
      id: 'no_status',
      status: intl.formatMessage(messages.noStatusTitle),
      description: intl.formatMessage(messages.noStatusDescription),
      value: 'no_status',
    } as ReviewStatusOptions;

    const requestChanges = {
      id: 'changes_requested',
      status: intl.formatMessage(messages.requestChangesTitle),
      description: intl.formatMessage(messages.requestChangesDescription),
      value: 'changes_requested',
    } as ReviewStatusOptions;

    const approve = {
      id: 'approved',
      status: intl.formatMessage(messages.approveTitle),
      description: intl.formatMessage(messages.approveDescription),
      value: 'approved',
    } as ReviewStatusOptions;

    let reviewStatuses: Array<ReviewStatusOptions>;

    if (pullRequest?.state === 'OPEN') {
      reviewStatuses = [noStatus, requestChanges, approve];
    } else {
      reviewStatuses = [noStatus, approve];
    }

    const hasApproved = useSelector(getHasCurrentUserApprovedPr);
    const hasRequestedChanges = useSelector(getIsChangesRequested);

    const defaultStatusValue: StatusValue = hasApproved
      ? 'approved'
      : hasRequestedChanges && pullRequest?.state === 'OPEN'
      ? 'changes_requested'
      : 'no_status';

    const [selectedStatus, setSelectedStatus] =
      useState<StatusValue>(defaultStatusValue);

    const openPendingDialog = () => {
      publishLinkClickEvent('ReviewDraft');
      dispatch({ type: OPEN_REVIEW_DRAFT_DIALOG });
    };

    const submitReview = async () => {
      if (owner && slug && id) {
        const url = urls.api.internal.review(owner, slug, id);
        // Setting selectedStatus = undefined will cause JSON.stringify
        // to remove it from the requestBody, which we want to do for PR
        // authors
        const status: StatusValue | undefined = !isPRAuthor
          ? selectedStatus
          : undefined;

        const requestBody = {
          status_change: status,
          node: sourceBranchHash,
        };

        publishButtonClickEvent('SubmitReview');

        // IR tracking attributes for event: `button clicked pullRequestScreenSubmitReview`
        publishButtonClickEvent('SubmitIRReview', { irTracking });
        setIsLoading(true);

        try {
          const response = await fetch(
            authRequest(url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(requestBody),
            })
          );

          if (!response.ok) {
            setIsLoading(false);
            if (response.status === 403) {
              throw new Error(intl.formatMessage(messages.permissionsError));
            }

            if (response.status === 400) {
              const json = await response.json();
              const code = json.error?.code;

              // 'NODE_REQUIRED' and 'INVALID_STATUS_CHANGE' errors will prompt the general error message
              if (code === 'CANNOT_REQUEST_CHANGES_MERGED_PR') {
                throw new Error(
                  intl.formatMessage(messages.requestChangesError)
                );
              }
            }

            throw new Error();
          }

          setIsLoading(false);
          setIsOpen(false);

          updateLastReviewedCommit(sourceBranchHash, owner, slug, id);
          // Publish a success event to trigger sagas to mark the iterative review as completed.
          dispatch({
            type: ITERATIVE_REVIEW_COMPLETED.TRUE,
          });

          showSuccessFlag('', intl.formatMessage(messages.successTitle));

          publishTrackEvent({
            action: 'submitted',
            actionSubject: 'pullRequestReview',
            attributes: {
              prStatus: pullRequest?.state || '',
              commentCount: numPendingComments,
              taskCount: numPendingTasks,
              reviewStatus: selectedStatus,
            },
          });

          dispatch(
            refreshCodeReviewData({
              needsDiff: false,
              needsPullRequest: true,
              needsComments: true,
              needsTasks: true,
              forceNeedsMergeChecks: false,
            })
          );
        } catch (e) {
          const errorMessage = e.message;
          setIsLoading(false);
          setIsOpen(false);
          dispatch(
            showFlagComponent('submit-review-error', {
              props: { errorMessage },
            })
          );
        }
      }
    };

    const FinishReviewPopup = (
      <styles.FinishReviewPopup>
        <styles.PopupTitle>
          {intl.formatMessage(messages.popupTitle)}
        </styles.PopupTitle>
        <Button appearance="link" spacing="none" onClick={openPendingDialog}>
          {intl.formatMessage(messages.pendingLink)}
        </Button>
        <Form onSubmit={() => {}}>
          {({ formProps }) => (
            <form {...formProps} style={{ margin: '0', width: '100%' }}>
              {!isPRAuthor && (
                <Field
                  label={intl.formatMessage(messages.statusFormTitle)}
                  name="review status"
                >
                  {() =>
                    reviewStatuses.map(reviewStatus => (
                      <styles.StatusOption key={reviewStatus.id}>
                        <Radio
                          isChecked={reviewStatus.value === selectedStatus}
                          label={
                            <>
                              {reviewStatus.status}
                              <br />
                              <small>{reviewStatus.description}</small>
                            </>
                          }
                          onChange={() => setSelectedStatus(reviewStatus.value)}
                        />
                      </styles.StatusOption>
                    ))
                  }
                </Field>
              )}
              <FormFooter>
                <Button
                  appearance="subtle"
                  isDisabled={isLoading}
                  onClick={() => setIsOpen(false)}
                >
                  {intl.formatMessage(messages.cancelButton)}
                </Button>
                <LoadingButton
                  onClick={
                    numOutdatedComments
                      ? () => setOutdatedModalOpen(true)
                      : submitReview
                  }
                  appearance="primary"
                  type="submit"
                  isLoading={isLoading}
                >
                  {intl.formatMessage(messages.submitButton)}
                </LoadingButton>
              </FormFooter>
            </form>
          )}
        </Form>
      </styles.FinishReviewPopup>
    );

    const FinishReviewButton = (triggerProps: TriggerProps) => (
      <Button
        {...triggerProps}
        onClick={() => setIsOpen(true)}
        appearance="primary"
        iconAfter={
          <Badge appearance="primaryInverted">
            {numPendingComments + numPendingTasks}
          </Badge>
        }
      >
        {intl.formatMessage(messages.finishReview)}
      </Button>
    );

    return (
      <>
        <Popup
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          content={() => FinishReviewPopup}
          shouldFlip={false}
          placement="bottom"
          trigger={triggerProps => (
            <KeyboardShortcutTooltip
              text={messages.finishReview}
              shortcut="s"
              position="top"
            >
              {FinishReviewButton(triggerProps)}
            </KeyboardShortcutTooltip>
          )}
        />
        {outdatedModalOpen && (
          <OutdatedPendingCommentsModal
            numOutdatedComments={numOutdatedComments}
            onSubmit={submitReview}
            onClose={() => setOutdatedModalOpen(false)}
          />
        )}
      </>
    );
  },
  {
    source: 'pullRequestScreen',
  }
);
